import { detectedFindings, type DetectedFindingsSource } from "./inferenceStatus";
import { formatFindingName } from "./textFormatters";

/**
 * Structural subset of ReaderStudyItem needed for the upload worklist CSV —
 * kept hook-free so this util (and its tests) never import from src/hooks.
 */
export interface UploadCsvRow extends DetectedFindingsSource {
  study_uid: string;
  job_id: string;
  status: string;
  created_at: string;
  inference_message?: string;
}

const HEADER = "study_uid,job_id,uploaded_at,status,failure_reason,findings_count,findings";

// Terminal = the model ran; only then are findings_count/findings meaningful.
// (A non-terminal row's empty cells mean "not yet", not "none found".)
const TERMINAL = new Set(["completed", "failed", "cancelled"]);

// `;` is included alongside the RFC 4180 triggers (`"`, `,`, `\n`) because
// findings are joined with "; " and the test below expects that cell quoted;
// quoting is always legal, and it also protects `;`-delimiter locales.
function csvEscape(field: string): string {
  return /[",\n;]/.test(field) ? `"${field.replaceAll('"', '""')}"` : field;
}

/** One row per study; header always present. Column order fixed — see HEADER. */
export function buildUploadsCsv(studies: UploadCsvRow[]): string {
  const lines = studies.map((s) => {
    const terminal = TERMINAL.has(s.status);
    const findings = terminal ? detectedFindings(s).map(formatFindingName) : [];
    return [
      s.study_uid,
      s.job_id,
      s.created_at,
      s.status,
      s.inference_message ?? "",
      terminal ? String(findings.length) : "",
      findings.join("; "),
    ]
      .map(csvEscape)
      .join(",");
  });
  return [HEADER, ...lines].join("\n");
}

/** Trigger a browser download of `csv` as `filename`. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
